import {
  DefaultTool,
  EdgelessLegacySlotIdentifier,
} from '@blocksuite/affine-block-surface';
import {
  popMenu,
  popupTargetFromElement,
} from '@blocksuite/affine-components/context-menu';
import type { RootBlockModel } from '@blocksuite/affine-model';
import {
  EditPropsStore,
  ThemeProvider,
} from '@blocksuite/affine-shared/services';
import { WidgetComponent, WidgetViewExtension } from '@blocksuite/std';
import { GfxControllerIdentifier } from '@blocksuite/std/gfx';
import { autoPlacement, offset, shift } from '@floating-ui/dom';
import { computed, effect, signal } from '@preact/signals-core';
import { html, nothing, type TemplateResult } from 'lit';
import { literal, unsafeStatic } from 'lit/static-html.js';
import { Subject } from 'rxjs';

import {
  EDGELESS_DOCK_QUICK_TOOL_WIDTH,
  EDGELESS_DOCK_SENIOR_TOOL_WIDTH,
} from './config/consts.js';
import {
  closeActiveDockMenu,
  type EdgelessDockPort,
  type EdgelessToolbarSlots,
} from './context.js';
import {
  EDGELESS_DOCK_PRESENTATION_ENTRY,
  type EdgelessDock,
  type EdgelessDockEntryInfo,
  EdgelessDockSinkIdentifier,
  type EdgelessDockState,
} from './dock.js';
import {
  EDGELESS_DOCK_ENTRY,
  type EdgelessDockEntry,
  type EdgelessDockEntryScope,
} from './dock-entry.js';
import {
  type QuickTool,
  QuickToolIdentifier,
  type SeniorTool,
  SeniorToolIdentifier,
} from './extension/index.js';
import { compareSeniorTools } from './senior-tool-order.js';

/** The overflow menu's gap to its button. */
const OVERFLOW_MENU_GAP = 12;

interface BuiltEntry {
  info: EdgelessDockEntryInfo;
  content: TemplateResult;
  menu?: QuickTool['menu'];
}

export const EDGELESS_DOCK_SOURCE_WIDGET = 'edgeless-dock-source-widget';

/**
 * The canvas's dock content: builds the registered quick and senior tools and publishes them to the
 * app's dock (`EdgelessDockSinkIdentifier`), which lays them out next to the AI island. The widget
 * renders nothing itself.
 */
export class EdgelessDockSourceWidget extends WidgetComponent<RootBlockModel> {
  /** The app's row the entries live in; tools are built against it. */
  private readonly _row$ = signal<HTMLElement | null>(null);

  /** The width the dock may take; the presentation controls go dense below their needs. */
  private readonly _room$ = signal(0);

  /** Bumps when non-reactive canvas state changes (read-only, presentation settings). */
  private readonly _revision$ = signal(0);

  private readonly _locked$ = signal(false);

  private readonly _presentSettingMenuShow$ = signal(false);

  private readonly _presentFrameMenuShow$ = signal(false);

  private readonly _port: EdgelessDockPort = { activePopper: null };

  private readonly _slots: EdgelessToolbarSlots = { resize: new Subject() };

  private readonly _mounted = new Map<string, Set<EdgelessDockEntry>>();

  private get _gfx() {
    return this.std.get(GfxControllerIdentifier);
  }

  private readonly _presenting$ = computed(
    () => this._gfx.tool.currentToolName$.value === 'frameNavigator'
  );

  private readonly _entryScope: EdgelessDockEntryScope = {
    port: this._port,
    slots: this._slots,
    theme$: computed(() => this.std.get(ThemeProvider).theme$.value),
  };

  private readonly _built$ = computed((): BuiltEntry[] => {
    this._revision$.value;
    const row = this._row$.value;
    const block = this.block;
    if (!row || !block || this._presenting$.value || this.store.readonly) {
      return [];
    }
    const gfx = this._gfx;
    const current = gfx.tool.currentToolName$.value;
    const options = { block, gfx, toolbarContainer: row };
    const quick = Array.from(
      this.std.provider.getAll(QuickToolIdentifier).entries(),
      ([id, builder]) => ({ id, tool: builder(options) })
    )
      .filter(({ tool }) => tool.enable ?? true)
      .sort((a, b) => (b.tool.priority ?? 0) - (a.tool.priority ?? 0));
    const senior = Array.from(
      this.std.provider.getAll(SeniorToolIdentifier).entries(),
      ([id, builder]) => ({ id, tool: builder(options) })
    )
      .filter(({ tool }) => tool.enable ?? true)
      .map((entry, index) => ({ ...entry, index }))
      .sort(compareSeniorTools);
    return [
      ...quick.map(({ id, tool }) => quickEntry(id, tool, current)),
      ...senior.map(({ id, tool }) => seniorEntry(id, tool)),
    ];
  });

  private readonly _presentation$ = computed(() => {
    if (!this._presenting$.value || !this.block) return null;
    return html`<presentation-toolbar
      .edgeless=${this.block}
      .settingMenuShow=${this._presentSettingMenuShow$.value}
      .frameMenuShow=${this._presentFrameMenuShow$.value}
      .setSettingMenuShow=${(show: boolean) =>
        (this._presentSettingMenuShow$.value = show)}
      .setFrameMenuShow=${(show: boolean) =>
        (this._presentFrameMenuShow$.value = show)}
      .containerWidth=${this._room$.value}
    ></presentation-toolbar>`;
  });

  private readonly _state$ = computed((): EdgelessDockState => {
    this._revision$.value;
    const presenting = this._presenting$.value;
    return {
      mode: presenting ? 'presenting' : this.store.readonly ? 'none' : 'tools',
      entries: this._built$.value.map(({ info }) => info),
      locked: this._locked$.value,
      autoHide:
        presenting &&
        !!this.std.get(EditPropsStore).getStorage('presentHideToolbar') &&
        !this._presentSettingMenuShow$.value &&
        !this._presentFrameMenuShow$.value,
    };
  });

  private readonly _dock: EdgelessDock = {
    state$: this._state$,
    attach: row => this._attach(row),
    mount: (id, container) => this._mount(id, container),
    openOverflow: (target, ids) => this._openOverflow(target, ids),
    setRoom: width => {
      this._room$.value = width;
    },
  };

  private _contentOf(id: string): TemplateResult | null {
    if (id === EDGELESS_DOCK_PRESENTATION_ENTRY) {
      return this._presentation$.value;
    }
    return (
      this._built$.value.find(entry => entry.info.id === id)?.content ?? null
    );
  }

  private _attach(row: HTMLElement) {
    this._row$.value = row;
    const unregisterOverlay = this._gfx.viewport.registerOverlay(() => row);
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width: w, height: h } = entry.contentRect;
        this._slots.resize.next({ w, h });
      }
    });
    observer.observe(row);
    return () => {
      observer.disconnect();
      unregisterOverlay();
      if (this._row$.peek() === row) this._row$.value = null;
    };
  }

  private _mount(id: string, container: HTMLElement) {
    const element = document.createElement(EDGELESS_DOCK_ENTRY);
    element.scope = this._entryScope;
    element.content = this._contentOf(id);
    container.append(element);
    let mounted = this._mounted.get(id);
    if (!mounted) {
      mounted = new Set();
      this._mounted.set(id, mounted);
    }
    mounted.add(element);
    return () => {
      element.remove();
      this._mounted.get(id)?.delete(element);
    };
  }

  private _openOverflow(target: HTMLElement, ids: readonly string[]) {
    const items = this._built$
      .peek()
      .filter(entry => ids.includes(entry.info.id))
      .flatMap(entry => (entry.menu ? [entry.menu] : []));
    if (!items.length) return () => {};
    const menu = popMenu(popupTargetFromElement(target), {
      middleware: [
        autoPlacement({ allowedPlacements: ['top'] }),
        offset({ mainAxis: OVERFLOW_MENU_GAP }),
        shift({ crossAxis: true, padding: 8 }),
      ],
      options: {
        className: 'streamlain-edgeless-toolbar-disclosure-menu',
        preferDesktop: true,
        items,
      },
    });
    return () => menu.close();
  }

  override connectedCallback() {
    super.connectedCallback();
    const sink = this.std.getOptional(EdgelessDockSinkIdentifier);
    if (sink) this.disposables.add(sink.publish(this._dock));
    // Mounted entries follow their tools (active state, canvas mode).
    this.disposables.add(
      effect(() => {
        this._built$.value;
        this._presentation$.value;
        for (const [id, elements] of this._mounted) {
          const content = this._contentOf(id);
          elements.forEach(element => (element.content = content));
        }
      })
    );
    if (!this.block) return;
    this.disposables.add(
      this.block.bindHotKey(
        {
          Escape: () => {
            const gfx = this._gfx;
            if (gfx.selection.editing) return;
            const tool = gfx.tool.currentToolName$.peek();
            if (tool === 'frameNavigator') return;
            if (tool === 'default') {
              closeActiveDockMenu(this._port);
              return;
            }
            gfx.tool.setTool(DefaultTool);
          },
        },
        { global: true }
      )
    );
  }

  override firstUpdated() {
    const slots = this.std.get(EdgelessLegacySlotIdentifier);
    const bump = () => this._revision$.value++;
    this.disposables.add(slots.readonlyUpdated.subscribe(bump));
    this.disposables.add(
      slots.toolbarLocked.subscribe(locked => (this._locked$.value = locked))
    );
    this.disposables.add(
      this.std.get(EditPropsStore).slots.storageUpdated.subscribe(({ key }) => {
        if (key === 'presentHideToolbar') bump();
      })
    );
  }

  override render() {
    return nothing;
  }
}

function quickEntry(id: string, tool: QuickTool, current: string): BuiltEntry {
  return {
    info: {
      id: `quick:${id}`,
      kind: 'quick',
      name: tool.name ?? tool.type ?? id,
      overflows: !!tool.menu,
      active: tool.type === current,
      width: EDGELESS_DOCK_QUICK_TOOL_WIDTH,
    },
    content: tool.content,
    menu: tool.menu,
  };
}

function seniorEntry(id: string, tool: SeniorTool): BuiltEntry {
  return {
    info: {
      id: `senior:${id}`,
      kind: 'senior',
      name: tool.name,
      overflows: false,
      active: false,
      width: EDGELESS_DOCK_SENIOR_TOOL_WIDTH,
    },
    content: tool.content,
  };
}

export const edgelessDockSourceWidget = WidgetViewExtension(
  'affine:page',
  EDGELESS_DOCK_SOURCE_WIDGET,
  literal`${unsafeStatic(EDGELESS_DOCK_SOURCE_WIDGET)}`
);
